/**
 * AdminApiKeys
 * ------------
 * Gestión de API keys oficiales para que aplicaciones externas (apps móviles,
 * dashboards de partners, integraciones Zapier/n8n) consuman la API de DNF.
 *
 * Flujo de creación:
 *  1. Admin pulsa "Nueva API key", elige nombre + scopes.
 *  2. Backend devuelve la key FULL (única vez).
 *  3. UI muestra modal con la key + botón "Copiar" (no se podrá volver a ver).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  KeyRound, Plus, Copy, Trash2, AlertTriangle, ShieldOff, CheckCircle2, Loader2, ExternalLink,
} from "lucide-react";

const fmtFecha = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES"); } catch { return iso.slice(0, 16); }
};

export default function AdminApiKeys() {
  const [keys, setKeys] = useState([]);
  const [scopesDisponibles, setScopesDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [keyRecienCreada, setKeyRecienCreada] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/api-keys"),
        api.get("/admin/api-keys/scopes"),
      ]);
      setKeys(r1.data.keys || []);
      setScopesDisponibles(r2.data.scopes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando API keys");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const revocar = async (key) => {
    if (!window.confirm(`¿Revocar la API key "${key.nombre}"? Las apps que la usen dejarán de funcionar inmediatamente.`)) return;
    try {
      await api.post(`/admin/api-keys/${key.id}/revocar`);
      toast.success("Key revocada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error revocando");
    }
  };

  const borrar = async (key) => {
    if (!window.confirm(`¿Eliminar definitivamente "${key.nombre}"? Esta acción es irreversible y se pierde el histórico de uso.`)) return;
    try {
      await api.delete(`/admin/api-keys/${key.id}`);
      toast.success("Key eliminada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="admin-api-keys-page">
      <PageHeader
        title="API Keys"
        subtitle="Acceso programático a la API de DNF para apps externas / integraciones"
        icon={KeyRound}
        action={
          <div className="flex gap-2 flex-wrap">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer"
              className="text-xs px-3 py-1.5 rounded border border-zinc-700 text-zinc-300 hover:text-amber-300 hover:border-amber-500/40 inline-flex items-center gap-1.5"
              data-testid="link-swagger"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Ver Swagger / OpenAPI
            </a>
            <Button onClick={() => setOpenCreate(true)}
              data-testid="btn-nueva-key"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              <Plus className="w-4 h-4 mr-1.5" /> Nueva API key
            </Button>
          </div>
        }
      />

      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-xs text-cyan-100">
        <b>💡 Uso desde una app externa:</b>{" "}
        cada llamada debe incluir el header <code className="bg-zinc-900 px-1 rounded">Authorization: Bearer dnf_live_xxx</code>.
        Prueba el endpoint <code className="bg-zinc-900 px-1 rounded">GET /api/public/whoami</code> para verificar que tu key funciona.
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : keys.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-zinc-800 rounded-lg">
          <KeyRound className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
          <div className="text-zinc-400 text-sm">Aún no hay API keys creadas</div>
          <div className="text-zinc-600 text-xs mt-1">Pulsa "Nueva API key" para crear la primera</div>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/40 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-2.5">Nombre</th>
                <th className="text-left p-2.5">Prefijo</th>
                <th className="text-left p-2.5">Scopes</th>
                <th className="text-left p-2.5">Uso</th>
                <th className="text-left p-2.5">Creada</th>
                <th className="text-left p-2.5">Estado</th>
                <th className="p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const revoked = !!k.revoked_at;
                return (
                  <tr key={k.id} data-testid={`apikey-row-${k.id}`}
                      className={`border-b border-zinc-800/60 hover:bg-zinc-900/40 ${revoked ? "opacity-50" : ""}`}>
                    <td className="p-2.5 text-zinc-100">
                      <div className="font-semibold">{k.nombre}</div>
                      {k.descripcion && <div className="text-[10px] text-zinc-500">{k.descripcion}</div>}
                    </td>
                    <td className="p-2.5 font-mono text-xs text-zinc-400">{k.key_prefix}…</td>
                    <td className="p-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(k.scopes || []).slice(0, 4).map((s) => (
                          <span key={s} className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono">
                            {s}
                          </span>
                        ))}
                        {(k.scopes || []).length > 4 && (
                          <span className="text-[9px] text-zinc-500">+{k.scopes.length - 4}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-2.5 text-xs text-zinc-300">
                      {k.uso_count || 0} llamadas
                      {k.last_used_at && (
                        <div className="text-[10px] text-zinc-500">Última: {fmtFecha(k.last_used_at)}</div>
                      )}
                    </td>
                    <td className="p-2.5 text-[10px] text-zinc-500">{fmtFecha(k.created_at)}<br />por {k.created_by}</td>
                    <td className="p-2.5">
                      {revoked ? (
                        <span className="text-[10px] text-rose-400 inline-flex items-center gap-1">
                          <ShieldOff className="w-3 h-3" /> REVOCADA
                        </span>
                      ) : (
                        <span className="text-[10px] text-emerald-400 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> ACTIVA
                        </span>
                      )}
                    </td>
                    <td className="p-2.5 text-right">
                      <div className="inline-flex gap-1">
                        {!revoked && (
                          <Button size="sm" variant="ghost"
                            onClick={() => revocar(k)}
                            data-testid={`revocar-${k.id}`}
                            className="h-7 text-xs text-amber-400 hover:bg-amber-500/10">
                            <ShieldOff className="w-3 h-3 mr-1" /> Revocar
                          </Button>
                        )}
                        <Button size="sm" variant="ghost"
                          onClick={() => borrar(k)}
                          data-testid={`borrar-${k.id}`}
                          className="h-7 text-xs text-zinc-500 hover:text-red-400">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openCreate && (
        <CreateKeyDialog
          scopesDisponibles={scopesDisponibles}
          onClose={() => setOpenCreate(false)}
          onCreated={(full) => { setOpenCreate(false); setKeyRecienCreada(full); load(); }}
        />
      )}
      {keyRecienCreada && (
        <KeyCreatedDialog data={keyRecienCreada} onClose={() => setKeyRecienCreada(null)} />
      )}
    </div>
  );
}

function CreateKeyDialog({ scopesDisponibles, onClose, onCreated }) {
  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    scopes: ["leads:read"],
    allowed_origins: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleScope = (s) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
    }));
  };

  const submit = async () => {
    if (!form.nombre.trim()) { toast.error("Pon un nombre descriptivo"); return; }
    if (form.scopes.length === 0) { toast.error("Selecciona al menos un scope"); return; }
    setSubmitting(true);
    try {
      const origins = form.allowed_origins.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/api-keys", {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || null,
        scopes: form.scopes,
        allowed_origins: origins,
      });
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando key");
    } finally { setSubmitting(false); }
  };

  // Agrupar scopes por entidad
  const grupos = {};
  for (const s of scopesDisponibles) {
    if (s === "*") continue;
    const [ent] = s.split(":");
    if (!grupos[ent]) grupos[ent] = [];
    grupos[ent].push(s);
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-crear-key">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-amber-400" /> Nueva API key
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nombre</Label>
            <Input value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder='Ej: "App SaaS Multimarca"'
              data-testid="input-key-nombre"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label>Descripción (opcional)</Label>
            <Textarea rows={2} value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Para qué app, quién la administra, etc."
              data-testid="input-key-descripcion"
              className="bg-zinc-900 border-zinc-800" />
          </div>

          <div>
            <Label className="flex items-center justify-between">
              <span>Permisos (scopes)</span>
              <button type="button"
                onClick={() => setForm({ ...form, scopes: form.scopes.includes("*") ? [] : ["*"] })}
                data-testid="toggle-superadmin"
                className={`text-[10px] px-2 py-0.5 rounded border ${
                  form.scopes.includes("*")
                    ? "bg-red-500/20 border-red-500/40 text-red-300"
                    : "border-zinc-700 text-zinc-500 hover:text-amber-300"
                }`}>
                {form.scopes.includes("*") ? "★ Super-admin (TODO)" : "Marcar como super-admin"}
              </button>
            </Label>
            <div className="mt-2 space-y-3 max-h-72 overflow-y-auto pr-1">
              {form.scopes.includes("*") ? (
                <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Esta key tendrá acceso COMPLETO a tu API. Úsala solo si confías plenamente en la app que la recibe.
                </div>
              ) : Object.entries(grupos).map(([ent, scopes]) => (
                <div key={ent}>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{ent}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {scopes.map((s) => {
                      const sel = form.scopes.includes(s);
                      return (
                        <button type="button" key={s}
                          onClick={() => toggleScope(s)}
                          data-testid={`scope-${s}`}
                          className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                            sel
                              ? "bg-amber-500/20 border-amber-500/40 text-amber-200"
                              : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-100"
                          }`}>
                          {s.endsWith(":read") ? "👁" : "✏"} {s.split(":")[1]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Orígenes permitidos para CORS (opcional)</Label>
            <Input value={form.allowed_origins}
              onChange={(e) => setForm({ ...form, allowed_origins: e.target.value })}
              placeholder="https://app2.rotulaciondnf.com, https://partners.example.com"
              data-testid="input-key-origins"
              className="bg-zinc-900 border-zinc-800" />
            <div className="text-[10px] text-zinc-500 mt-1">
              Vacío = sin restricción CORS por origen. Separa varios por coma.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}
            data-testid="btn-cancelar-key"
            className="text-zinc-400">Cancelar</Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="btn-crear-key"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
            Generar API key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeyCreatedDialog({ data, onClose }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(data.full_key_SOLO_AHORA);
      setCopiado(true);
      toast.success("API key copiada al portapapeles");
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      toast.error("No se pudo copiar — selecciona y copia manualmente");
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-amber-500/40 text-zinc-100 max-w-xl"
        data-testid="dialog-key-creada">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" /> API key creada
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/40 rounded p-3 text-xs text-amber-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <b>Copia esta key AHORA.</b> Por seguridad no se guarda en claro y no podrás volver a verla. Si la pierdes tendrás que revocarla y crear una nueva.
            </div>
          </div>

          <div>
            <Label>Tu API key</Label>
            <div className="flex gap-2 mt-1">
              <Input
                readOnly
                value={data.full_key_SOLO_AHORA}
                data-testid="full-key-input"
                onClick={(e) => e.target.select()}
                className="bg-black border-amber-500/40 font-mono text-emerald-300 text-xs"
              />
              <Button onClick={copiar}
                data-testid="btn-copiar-key"
                className={copiado
                  ? "bg-emerald-500 text-zinc-900"
                  : "bg-amber-500 hover:bg-amber-400 text-zinc-900"}>
                <Copy className="w-4 h-4 mr-1" /> {copiado ? "Copiado!" : "Copiar"}
              </Button>
            </div>
          </div>

          <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3 text-xs">
            <div className="text-zinc-400 mb-1.5">Ejemplo de uso (curl):</div>
            <pre className="bg-black border border-zinc-800 rounded p-2 text-[10px] text-cyan-300 overflow-x-auto">
{`curl https://app.rotulaciondnf.com/api/public/whoami \\
     -H "Authorization: Bearer ${data.full_key_SOLO_AHORA}"`}
            </pre>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}
            data-testid="btn-cerrar-key-creada"
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100">
            Ya la he guardado, cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
